import React, { useState, useRef } from 'react'
import axios from 'axios'
import Imgix from 'react-imgix'
import { Row, Col, Form as RBForm, Container } from 'react-bootstrap'
import classNames from 'classnames'
import CKEditor from '../components/editor'
import { Formik } from 'formik'
import { string, object } from 'yup'
import {
  useResolutionService,
  Button,
  useNotificationService,
  useErrorService,
  CloseIcon,
} from '@abroad/components'
import API from '../utils/API'
import { PageHeader } from '../components'
import { InputField } from '../components/form'
import { useHistory } from 'react-router-dom'
import ImageCropModal from '../components/common/ImageCropModal'

const validationSchema = object().shape({
  title: string()
    .max(200, 'Title must be at most 200 characters')
    .required('Required'),
  author: string()
    .max(100, 'Author must be at most 100 characters')
    .required('Required'),
  subTitle: string()
    .max(500, 'Short Description must be at most 500 characters')
    .required('Required'),
  type: string().oneOf(['written', 'link']).required('Required'),
  blog: string().when('type', {
    is: 'written',
    then: string().required('Required'),
    otherwise: string().nullable(),
  }),
  blogLink: string().when('type', {
    is: 'link',
    then: string().url('Invalid URL').required('Required'),
    otherwise: string().nullable(),
  }),
})

const CreateBlog = ({ blogDetail, blogId }) => {
  const isMDScreen = useResolutionService()
  const Notification = useNotificationService()
  const history = useHistory()
  const Error = useErrorService()
  const [isUploading, setIsUploading] = useState(false)
  const [file, setFile] = useState()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [blogImageUpload, setBlogImageUpload] = useState(false)
  const [showImageCropModal, setShowImageCropModal] = useState(false)
  const [selectedImageFile, setSelectedImageFile] = useState(null)
  const [isDraggingImage, setIsDraggingImage] = useState(false)
  const [blogDetails, setBlogDetails] = useState({
    title: blogDetail?.title || '',
    author: blogDetail?.author || '',
    subTitle: blogDetail?.subTitle || '',
    image: blogDetail?.image || '',
    blog: blogDetail?.blog || '',
  })
  const imageRef = useRef()
  const inputResetRef = useRef(null)

  const getTitle = () => {
    if (blogId) {
      return (
        <div className='d-flex'>
          <div
            onClick={() => history.replace('/admin/experiences-media/blogs')}
            className='cursor-pointer'>
            Blog
          </div>
          &nbsp;/&nbsp;
          <>{blogDetail.title ? `${blogDetail.title}` : ''}</>
        </div>
      )
    } else {
      return (
        <div className='d-flex'>
          <div
            onClick={() => history.replace('/admin/experiences-media/blogs')}
            className='cursor-pointer'>
            Blog
          </div>
          &nbsp;/ New
        </div>
      )
    }
  }

  const getImage = () => {
    if (imageRef.current) {
      imageRef.current.value = ''
      imageRef.current.click()
    }
  }

  const uploadImage = async (imageFile) => {
    try {
      const contentType = imageFile.name.split('.').pop().toUpperCase()
      const { data } = await API.AdminAPI.quests.getBlogImage(contentType)
      if (data) {
        await axios.put(data.url.url, imageFile, {
          headers: { 'Content-Type': data.url.contentType },
        })
        return data.fileName
      }
    } catch (e) {
      Error.showError(e)
      imageRef.current.value = ''
      throw new Error(e.message)
    }
  }

  const validateImageFile = (imageFile) => {
    const allowedFormats = ['JPG', 'JPEG', 'PNG']
    const fileExtension = imageFile?.name?.split('.').pop()?.toUpperCase()
    if (!allowedFormats.includes(fileExtension)) {
      Notification.showNotification(
        'Filetype must be one of [JPG, JPEG, PNG]',
        'danger',
      )
      return false
    }

    if (
      imageFile.type === 'image/webp' ||
      imageFile.name.toLowerCase().endsWith('.webp')
    ) {
      Notification.showNotification(
        'WebP format is not supported. Please use JPG, JPEG, or PNG format.',
        'danger',
      )
      return false
    }
    return true
  }

  const openCropModal = (imageFile, resetInput) => {
    if (!imageFile) return
    if (!validateImageFile(imageFile)) {
      if (resetInput) resetInput()
      return
    }
    inputResetRef.current = resetInput || null
    setSelectedImageFile(imageFile)
    setShowImageCropModal(true)
  }

  const resetImageInput = () => {
    if (typeof inputResetRef.current === 'function') {
      inputResetRef.current()
    }
    inputResetRef.current = null
  }

  const handleCropModalClose = () => {
    setShowImageCropModal(false)
    setSelectedImageFile(null)
    setIsDraggingImage(false)
    resetImageInput()
  }

  const handleDragEnter = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDraggingImage(true)
  }

  const handleDragOver = (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (!isDraggingImage) setIsDraggingImage(true)
  }

  const handleDragLeave = (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsDraggingImage(false)
    }
  }

  const handleDrop = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDraggingImage(false)
    const files = e.dataTransfer?.files
    if (files && files.length > 0) {
      openCropModal(files[0])
      e.dataTransfer.clearData()
    }
  }

  const handleRemoveImage = () => {
    setFile(null)
    setBlogDetails((prevDetails) => ({
      ...prevDetails,
      image: '',
    }))
    if (imageRef.current) {
      imageRef.current.value = ''
    }
    // Notification.showNotification('Image removed successfully!', 'success')
  }

  const onSubmit = async (values) => {
    try {
      setIsSubmitting(true)
      const isLinkType = values.type === 'link'
      const imageToUpload = blogDetails.image || file

      // ✅ Validation for missing image
      if (!imageToUpload) {
        Notification.showNotification(
          'Please select an image to upload.',
          'danger',
        )
        setIsSubmitting(false)
        return
      }
      const reqData = {
        title: values.title,
        author: values.author,
        subTitle: values.subTitle,
        image: imageToUpload,
        type: values.type,
        blog: isLinkType ? undefined : values.blog,
        blogLink: isLinkType ? values.blogLink : undefined,
      }

      if (blogId) {
        const { data } = await API.AdminAPI.quests.getUpdateBlog(
          blogId,
          reqData,
        )
        setIsSubmitting(false)
        if (data) {
          Notification.showNotification('Blog updated!', 'success')
          history.push('/admin/experiences-media/blogs')
        }
      } else {
        const { data } = await API.AdminAPI.quests.getCreateBlog(reqData)
        setIsSubmitting(false)
        if (data) {
          Notification.showNotification('Blog created!', 'success')
          history.push('/admin/experiences-media/blogs')
        }
      }
    } catch (err) {
      Error.showError(err)
      setIsSubmitting(false)
    }
  }

  return (
    <main>
      <PageHeader titleText={getTitle()} hasBackArrow={true} />
      <Formik
        initialValues={
          blogDetail
            ? {
                ...blogDetail,
                type: blogDetail.type || 'written',
                blogLink: blogDetail.blogLink || '',
              }
            : {
                title: '',
                subTitle: '',
                author: '',
                blog: '',
                type: 'written',
                blogLink: '',
                image: '',
              }
        }
        validationSchema={validationSchema}
        onSubmit={onSubmit}
        enableReinitialize>
        {({ handleSubmit, setFieldValue, dirty, isValid, values }) => {
          return (
            <Container
              className='pt-2 custom-offset-lg-1 custom-col-lg-12 px-lg-0'
              fluid>
              <RBForm onSubmit={handleSubmit}>
                <Row className='mt-5'>
                  <Col lg={6}>
                    <div className='mb-3 mt-2'>
                      <div className='d-flex justify-content-between'>
                        <label className='s8'>Blog Image</label>
                      </div>
                      <div
                        onClick={getImage}
                        onDragEnter={handleDragEnter}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                        className='quest-header-image cursor-pointer position-relative'
                        style={{
                          aspectRatio: isMDScreen ? '2/1' : '4/2',
                          minHeight: isMDScreen ? '200px' : '150px',
                          backgroundColor: '#f8f9fa',
                          border: isDraggingImage
                            ? '2px dashed #f4b62a'
                            : '2px dashed transparent',
                          borderRadius: '10px',
                          transition: 'border-color 0.2s ease',
                        }}>
                        {/* Hover overlay for update text - only show when not uploading */}
                        {!isUploading && (
                          <div
                            className='position-absolute w-100 h-100 d-flex align-items-center justify-content-center'
                            style={{
                              backgroundColor: 'rgba(0, 0, 0, 0.6)',
                              opacity: 0,
                              transition: 'opacity 0.3s ease',
                              zIndex: 5,
                              borderRadius: '10px',
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.opacity = 1
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.opacity = 0
                            }}>
                            <span className='text-saffron-700 s8 font-weight-bold'>
                              {isDraggingImage
                                ? 'Drop image to upload'
                                : file || blogDetails.image
                                ? 'Click to update'
                                : 'Upload Image'}
                            </span>
                          </div>
                        )}
                        {isUploading && (
                          <div className='text-saffron-700 uploading-text-quest'>
                            Uploading...
                          </div>
                        )}
                        {file ? (
                          <>
                            {/* Close icon for removing local image */}
                            <div
                              className='rounded-circle quest-close-icon position-absolute top-0 right-0 p-2'
                              style={{ zIndex: 10 }}
                              onClick={(e) => {
                                e.stopPropagation()
                                e.preventDefault()
                                handleRemoveImage()
                              }}>
                              <CloseIcon
                                className='d-flex align-items-center justify-content-center'
                                fontSize='10px'
                                color='#000'
                                discardMargin={true}
                              />
                            </div>
                            <img
                              src={file}
                              alt='local'
                              style={{
                                aspectRatio: isMDScreen ? '2/1' : '4/2',
                                objectFit: 'cover',
                                width: '100%',
                                height: '100%',
                              }}
                              className={classNames(
                                'border-radius-10 questImage-container lazyload',
                                {
                                  itenaryImage: blogImageUpload,
                                },
                              )}
                            />
                          </>
                        ) : blogDetails.image ? (
                          <>
                            {/* Close icon for removing uploaded image */}
                            <div
                              className='rounded-circle quest-close-icon position-absolute top-0 right-0 p-2'
                              style={{ zIndex: 10 }}
                              onClick={(e) => {
                                e.stopPropagation()
                                e.preventDefault()
                                handleRemoveImage()
                              }}>
                              <CloseIcon
                                className='d-flex align-items-center justify-content-center'
                                fontSize='10px'
                                color='#000'
                                discardMargin={true}
                              />
                            </div>
                            <Imgix
                              imgixParams={{
                                fit: 'crop',
                                crop: 'format',
                                ar: isMDScreen ? '2/1' : '4/2',
                              }}
                              src={`${process.env.REACT_APP_IMG_SOURCE}/blogs/${blogDetails.image}`}
                              attributeConfig={{
                                src: 'data-src',
                                srcSet: 'data-srcset',
                                sizes: 'data-sizes',
                              }}
                              htmlAttributes={{
                                src: `${
                                  process.env.REACT_APP_IMG_SOURCE
                                }/blogs/${blogDetails.image}?ar=${
                                  isMDScreen ? '2/1' : '4/2'
                                }&fit=crop&crop=center&fill=blur&blur=300&px=16&auto=format`,
                              }}
                              width='100%'
                              height='100%'
                              alt='Uploaded image'
                              className={classNames(
                                'border-radius-10 questImage-container lazyload w-100 h-100',
                                {
                                  itenaryImage: blogImageUpload,
                                },
                              )}
                              style={{
                                objectFit: 'cover',
                                aspectRatio: isMDScreen ? '2/1' : '4/2',
                              }}
                            />
                          </>
                        ) : null}
                        <RBForm.Control
                          type='file'
                          size='lg'
                          placeholder='Select File'
                          accept='image/jpg, image/jpeg, image/png'
                          onChange={(e) => {
                            const target = e.target
                            const files = e.target.files
                            if (files && files.length > 0) {
                              openCropModal(files[0], () => {
                                if (target) target.value = ''
                              })
                            }
                          }}
                          style={{ display: 'none' }}
                          ref={imageRef}
                        />
                      </div>
                    </div>
                    <ImageCropModal
                      show={showImageCropModal}
                      onHide={handleCropModalClose}
                      file={selectedImageFile}
                      onConfirm={async (croppedImageFile) => {
                        if (!croppedImageFile) return
                        setShowImageCropModal(false)
                        try {
                          setIsUploading(true)
                          setBlogImageUpload(true)
                          setFile(URL.createObjectURL(croppedImageFile))
                          const fileName = await uploadImage(croppedImageFile)
                          setBlogDetails((prevDetails) => ({
                            ...prevDetails,
                            image: fileName,
                          }))
                          setIsUploading(false)
                        } catch (e) {
                          setIsUploading(false)
                          setFile(null)
                          Error.showError(e)
                        } finally {
                          setBlogImageUpload(false)
                          setSelectedImageFile(null)
                          setIsDraggingImage(false)
                          resetImageInput()
                        }
                      }}
                      isUploading={isUploading}
                      aspect={isMDScreen ? 2 / 1 : 4 / 2}
                      title='Crop Image'
                    />
                  </Col>
                  <Col lg={6}>
                    <label htmlFor='title' className='s8'>
                      TITLE
                    </label>
                    <InputField
                      id='title'
                      type='text'
                      name='title'
                      size='lg'
                      showErrorMessage={true}
                    />
                    <label htmlFor='author' className='s8'>
                      AUTHOR
                    </label>
                    <InputField
                      id='author'
                      name='author'
                      size='lg'
                      showErrorMessage={true}
                    />
                    <label htmlFor='subTitle' className='s8'>
                      SHORT DESCRIPTION
                    </label>
                    <InputField
                      as='textarea'
                      id='subTitle'
                      name='subTitle'
                      size='lg'
                      showErrorMessage={true}
                    />
                    <div className='mt-4'>
                      <label className='s8 d-block'>BLOG TYPE</label>
                      <div className='d-flex'>
                        <RBForm.Check
                          type='radio'
                          id='blogType-written'
                          name='type'
                          label='Written'
                          className='mr-3 s6'
                          checked={values.type === 'written'}
                          onChange={() => setFieldValue('type', 'written')}
                        />
                        <RBForm.Check
                          className='s6'
                          type='radio'
                          id='blogType-link'
                          name='type'
                          label='Link'
                          checked={values.type === 'link'}
                          onChange={() => setFieldValue('type', 'link')}
                        />
                      </div>
                    </div>
                  </Col>
                </Row>
                <Row>
                  <Col lg={12} className='mt-3'>
                    {values.type === 'written' ? (
                      <>
                        <label htmlFor='blog' className='s8 my-2'>
                          DESCRIPTION
                        </label>
                        <CKEditor
                          name='blog'
                          data={blogDetails.blog}
                          onChange={(event, editor) => {
                            setFieldValue('blog', editor.getData())
                          }}
                          blog={true}
                        />
                      </>
                    ) : (
                      <>
                        <label htmlFor='blogLink' className='s8 my-2'>
                          BLOG URL
                        </label>
                        <InputField
                          id='blogLink'
                          name='blogLink'
                          size='lg'
                          // placeholder='https://example.com/blog-post'
                          className='placeholder-opacity-50'
                          showErrorMessage={true}
                        />
                      </>
                    )}
                    <div className='text-right mt-3'>
                      <Button
                        variant='gray'
                        size='md'
                        className='line-height-normal border-radius-3 text-uppercase'
                        type='submit'
                        disabled={
                          blogId
                            ? isSubmitting
                            : !(isValid && dirty) ||
                              (!file && !blogDetails.image) ||
                              isSubmitting
                        }>
                        {blogId ? 'UPDATE' : 'SAVE'}
                      </Button>
                    </div>
                  </Col>
                </Row>
              </RBForm>
            </Container>
          )
        }}
      </Formik>
    </main>
  )
}

export default CreateBlog
